/**
 * Authentication Context Provider
 *
 * Manages server-side session authentication via Keystone Platform backend.
 * Uses FastAPI backend OAuth flow with full-page redirect to Microsoft login.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react'

// User interface matching backend response
export interface User {
  name: string
  email: string
  roles: string[]
  [key: string]: unknown
}

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: () => Promise<void>
  logout: () => Promise<void>
}

// Get backend API URL from environment
const getBackendUrl = (): string => {
  // In production, use the backend URL from environment
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  // In development, use relative paths (proxied by vite.config.ts)
  return ''
}

// Create the authentication context
const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Authentication Context Provider Component
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Check authentication status with backend
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/auth/status`, {
        credentials: 'include', // Important: include cookies
      })

      if (!response.ok) {
        throw new Error('Failed to check auth status')
      }

      const data = await response.json()

      if (data.authenticated && data.user) {
        setUser(data.user)
        setIsAuthenticated(true)
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (err) {
      console.error('AuthContext: Error checking auth status:', err)
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Initialize authentication check on mount
   * Also handles redirect-based OAuth callback (token exchange)
   */
  useEffect(() => {
    const handleRedirectCallback = async () => {
      // Check for auth_token in both regular query params and hash-based routing
      let authToken = new URLSearchParams(window.location.search).get('auth_token')

      // If not in search params, check in the hash (for HashRouter)
      if (!authToken && window.location.hash.includes('auth_token=')) {
        const hashParts = window.location.hash.split('?')
        if (hashParts.length > 1) {
          const hashParams = new URLSearchParams(hashParts[1])
          authToken = hashParams.get('auth_token')
        }
      }

      if (authToken) {
        // Redirect flow: exchange token for session

        // Remove the auth_token param from URL to clean up
        if (window.location.hash.includes('auth_token=')) {
          const hashParts = window.location.hash.split('?')
          const cleanHash = hashParts[0]
          window.history.replaceState({}, '', window.location.pathname + cleanHash)
        } else {
          const urlParams = new URLSearchParams(window.location.search)
          urlParams.delete('auth_token')
          const newSearch = urlParams.toString()
          const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash
          window.history.replaceState({}, '', newUrl)
        }

        try {
          const backendUrl = getBackendUrl()
          const response = await fetch(`${backendUrl}/api/auth/exchange-token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: authToken }),
          })

          if (response.ok) {
            const data = await response.json()
            if (data.authenticated && data.user) {
              setUser(data.user)
              setIsAuthenticated(true)
              setIsLoading(false)
              return
            }
          }
        } catch (err) {
          console.error('AuthContext: Token exchange error:', err)
        }

        // Fallback to checking auth status
        await checkAuthStatus()
      } else {
        // Normal page load - just check auth status
        checkAuthStatus()
      }
    }

    handleRedirectCallback()
  }, [checkAuthStatus])

  /**
   * Login function - full-page redirect to Azure AD
   */
  const login = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    const backendUrl = getBackendUrl()
    const returnUrl = window.location.origin + '/'
    // Prod: /api prefix is required — the connect SWA proxy only forwards
    // /api/* to the linked backend. Local dev keeps the Vite-proxied path.
    const loginUrl = backendUrl
      ? `${backendUrl}/api/azure-login?mode=redirect&return_url=${encodeURIComponent(returnUrl)}`
      : `/azure-login?mode=redirect&return_url=${encodeURIComponent(returnUrl)}`

    window.location.href = loginUrl

    // Page will redirect — return a never-resolving promise
    return new Promise<void>(() => {})
  }, [])

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true)

      const backendUrl = getBackendUrl()
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })

      // Clear all site storage and cookies for a fresh start
      localStorage.clear()
      sessionStorage.clear()
      document.cookie.split(';').forEach((c) => {
        document.cookie = c.trim().split('=')[0] + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'
      })

      setUser(null)
      setIsAuthenticated(false)
    } catch (err) {
      console.error('AuthContext: Logout error:', err)
      // Still clear storage on error
      localStorage.clear()
      sessionStorage.clear()
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      error,
      login,
      logout,
    }),
    [user, isAuthenticated, isLoading, error, login, logout]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

/**
 * Custom hook to use authentication context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

export default AuthContext
